import { useRef, useState, KeyboardEvent, ClipboardEvent } from 'react';
import { cn } from '@/lib/utils';

interface IProps {
  value: string;
  onChange: (value: string) => void;
  length?: number;
  error?: boolean;
}

export function CustomOTP({ value, onChange, length = 6, error = false }: IProps) {
  const inputsRef = useRef<(HTMLInputElement | null)[]>([]);
  const [activeIndex, setActiveIndex] = useState<number>(-1);
  const [slots, setSlots] = useState<string[]>(() => {
    const chars = value.split('');
    return Array.from({ length }, (_, i) => chars[i] || '');
  });

  const updateSlots = (newSlots: string[]) => {
    setSlots(newSlots);
    onChange(newSlots.join(''));
  };

  const focusInput = (index: number) => {
    if (index < 0 || index >= length) return;
    setActiveIndex(index);
    inputsRef.current[index]?.focus();
    inputsRef.current[index]?.select();
  };

  const handleChange = (index: number, char: string) => {
    const digits = char.replace(/\D/g, '');

    if (digits.length > 1) {
      const trimmed = digits.slice(0, length);
      const newSlots = Array.from({ length }, (_, i) => trimmed[i] || '');
      updateSlots(newSlots);
      if (trimmed.length >= length) {
        inputsRef.current.forEach(el => el?.blur());
        (document.activeElement as HTMLElement)?.blur();
        setActiveIndex(-1);
      } else {
        const nextIndex = Math.min(trimmed.length, length - 1);
        focusInput(nextIndex);
      }
      return;
    }

    const digit = digits.slice(-1);
    if (!digit) return;

    const newSlots = [...slots];
    newSlots[index] = digit;
    updateSlots(newSlots);

    if (digit) {
      const nextEmpty = newSlots.findIndex((s, i) => i > index && !s);
      if (nextEmpty !== -1) {
        focusInput(nextEmpty);
      } else if (index < length - 1) {
        focusInput(index + 1);
      } else {
        const allFilled = newSlots.every(s => s);
        if (allFilled) {
          inputsRef.current[index]?.blur();
          setActiveIndex(-1);
        }
      }
    }
  };

  const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      e.preventDefault();

      if (slots[index]) {
        const newSlots = [...slots];
        newSlots[index] = '';
        updateSlots(newSlots);
      } else if (index > 0) {
        const newSlots = [...slots];
        newSlots[index - 1] = '';
        updateSlots(newSlots);
        focusInput(index - 1);
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      e.preventDefault();
      focusInput(index - 1);
    } else if (e.key === 'ArrowRight' && index < length - 1) {
      e.preventDefault();
      focusInput(index + 1);
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length);
    if (!pasted) return;
    const newSlots = Array.from({ length }, (_, i) => pasted[i] || '');
    updateSlots(newSlots);
    const nextIndex = Math.min(pasted.length, length - 1);
    focusInput(nextIndex);
  };

  return (
    <div className="flex items-center gap-2 justify-center">
      {Array.from({ length }).map((_, index) => (
        <input
          key={index}
          ref={el => { inputsRef.current[index] = el; }}
          type="tel"
          name={index === 0 ? 'otp' : undefined}
          autoComplete={index === 0 ? 'one-time-code' : 'off'}
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={index === 0 ? length : 1}
          value={slots[index]}
          onChange={e => handleChange(index, e.target.value)}
          onKeyDown={e => handleKeyDown(index, e)}
          onPaste={handlePaste}
          onFocus={() => setActiveIndex(index)}
          onBlur={() => setActiveIndex(-1)}
          onClick={() => {
            setActiveIndex(index);
            inputsRef.current[index]?.select();
          }}
          className={cn(
            'rounded-xl border-2 bg-fill-base text-center font-medium text-text-brand-tertiary outline-none transition-all caret-transparent',
            length <= 4 ? 'h-[60px] w-[52px] text-4xl' : 'h-[52px] w-[44px] text-3xl',
            activeIndex === index
              ? 'border-brand-green ring-2 ring-offset-2 z-10'
              : error && !slots[index]
                ? 'border-red-500'
                : 'border-border-primary'
          )}
        />
      ))}
    </div>
  );
}
